import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { globalSearch, GlobalSearchResult } from '../../api/api';

// Use the GlobalSearchResult type from the API
export type SearchResult = GlobalSearchResult;

export interface UseGlobalSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  results: SearchResult[];
  isLoading: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedIndex: number;
  handleKeydown: (event: React.KeyboardEvent) => void;
  selectResult: (result: SearchResult) => void;
  clearSearch: () => void;
}

export const useGlobalSearch = (): UseGlobalSearchReturn => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const navigate = useNavigate();

  // Perform the actual search
  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);

    try {
      // Use the new global search API that searches both artists and people
      const searchResponse = await globalSearch(searchQuery, ['people', 'artists'], 10);
      setResults(searchResponse.results);
      setIsOpen(searchResponse.results.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error performing global search:', error);
      setResults([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search function
  const debouncedSearch = useRef(debounce(performSearch, 300)).current;

  // Effect to trigger search when query changes
  useEffect(() => {
    if (query.length >= 2) {
      debouncedSearch(query);
    } else {
      setResults([]);
      setIsOpen(false);
      setSelectedIndex(-1);
    }
  }, [query, debouncedSearch]);

  // Clear search state
  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  }, []);

  // Navigate to the selected result
  const selectResult = useCallback((result: SearchResult) => {
    if (result.type === 'artist') {
      navigate(`/artist/${result.id}`);
    } else if (result.type === 'person') {
      navigate(`/u/${result.id}`);
    }
    clearSearch();
  }, [navigate, clearSearch]);

  // Handle keyboard navigation
  const handleKeydown = useCallback((event: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : results.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          selectResult(results[selectedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        clearSearch();
        break;
    }
  }, [isOpen, results, selectedIndex, selectResult, clearSearch]);

  return {
    query,
    setQuery,
    results,
    isLoading,
    isOpen,
    setIsOpen,
    selectedIndex,
    handleKeydown,
    selectResult,
    clearSearch,
  };
};